import dynamic from 'next/dynamic';
import { getEntries } from '../../../utils/api/contentful';
import withAppProps, { WithPageAppProps } from '../../../utils/withAppProps';
import { ImageProps } from '../../../common-components/component-library/Image';
import { getApproaches, getInitiatives, getPillars } from '../../../utils/api/content';
import HighlightLayout, {
  type HighlightButton,
} from '../../../common-components/component-library/layout/HighlightLayout';
import FundHero from '../../../common-components/component-library/heroes/FundHero';
import PartnerLogosLayout from '../../../common-components/component-library/layout/PartnerLogosLayout';
import {
  TypeBlogPostSkeleton,
  TypeImpactFundTemplate,
  TypeImpactFundTemplateSkeleton,
  TypeInitiativeSkeleton,
  TypePillarSkeleton,
} from '../../../../@types/generated';
import CTASectionLayout from '../../../common-components/component-library/layout/CTASectionLayout';
import OurApproachLayout from '../../../common-components/component-library/layout/ourApproachLayout/OurApproachLayout';
import { Document } from '@contentful/rich-text-types';
import { colors } from '../../../utils/styleguide';
import HighlightedProfilesLayout from '../../../common-components/component-library/layout/highlightedProfilesLayout/HighlightedProfilesLayout';
import QuestionsAndAnswers from '../../../common-components/component-library/layout/accordionLayout/QuestionsAndAnswers';
import FundPillars, {
  type FundPillarAccordionData,
} from '../../../common-components/component-library/layout/accordionLayout/FundPillars';
import { UrlError } from '../../../utils/error';
import FormCTA, {
  toFormCTASection,
} from '../../../common-components/component-library/layout/FormCTA';
import PartnerCasesLayout from '../../../common-components/component-library/layout/partnerCasesLayout/PartnerCasesLayout';
import CarouselLayout from '../../../common-components/component-library/layout/carouselLayout/CarouselLayout';
import { Asset } from 'contentful';
import ProjectListingWithPreview from '../../../common-components/component-library/layout/ProjectList/ProjectListingWithPreview';
import { getLocationStrings } from '../../../utils/helpers/locations';
import { getLocations } from '../../../utils/api/location';
import { PILLAR_COLORS } from '../../../utils/constants/constants';
import { transformMetaHeaderType } from '../../../common-components/contentful-elements/MetaHeader';
import { hasValue } from '../../../utils/typeCheck';
import LazyComponent, { loading } from '../../../common-components/component-library/LazyComponent';
import { linksToButtons } from '../../../utils/templatePages/linksToButtons';
import { toImageFromContentfulMedia } from '../../../utils/helpers/toImageFromContentfulMedia';
import isCDRCategoryId from '../../../utils/categories';
import { getReadTime } from '../../../domains/articles/articleUtils';
import { getLinkHref } from '../../../common-components/contentful-elements/util/linkHref';

const MapMetricsLayout = dynamic(
  () => import('../../../common-components/component-library/layout/MapMetricsLayout'),
  {
    ssr: false,
    loading: loading({
      height: '600px',
    }),
  },
);

export type ImpactFundTemplateProps = {
  heroCTAs?: { label?: string; href?: string }[];
  heroImage?: { credit?: string; url: string; alt: string };
  heroParagraph?: string;
  title: string;
  partnerLogosTitle?: string;
  partnerLogos?: {
    link?: string;
    image?: ImageProps;
    alt?: string;
  }[];
  introHeadline?: string;
  description?: string;
  pitchSectionTitle?: string;
  pitchText?: Document;
  ourSolutionButtons?: HighlightButton[];
  fundPillarsWithData: Array<
    FundPillarAccordionData & {
      icon?: {
        url: string;
        alt: string;
      };
      locationCodes: string[];
    }
  >;
  ctaSectionTitle?: string;
  ctaSectionText?: string;
  ctaSectionButtons?: Array<{
    label?: string;
    href?: string;
  }>;
  ctaSectionBackgroundImage?: {
    url?: string;
    alt?: string;
  };
  campaignSectionTitle?: string;
  campaignText?: Document;
  campaignButtons?: HighlightButton[];
  campaignImage?: {
    url: string;
    alt: string;
    credit?: string;
  };
  campaignImageFit: TypeImpactFundTemplate['fields']['campaignImageFit'];
  ourApproachSectionTitle?: string;
  ourApproachText?: Document;
  ourApproachLogos?: {
    link?: string;
    image?: ImageProps;
  }[];
  ourApproachSteps?: {
    title?: string;
    description?: Document;
  }[];
  fundPillarsSectionTitle?: string;
  fundPillarsTitle?: string;
  fundPillarsParagraph?: string;
  faqSectionTitle?: string;
  faqSectionReferences?: Array<{
    title?: string;
    description?: Document;
  }>;
  fundManagerSectionTitle?: string;
  fundManagerTitle?: string;
  fundManagerParagraph?: string;
  fundManagerHeadline?: string;
  advisoryGroupHeadline?: string;
  fundManagerProfiles?: {
    name?: string;
    title?: string;
    description?: Document;
    imageUrl?: string;
    imageAlt?: string;
  }[];
  advisoryGroupProfiles?: {
    name?: string;
    title?: string;
    imageUrl?: string;
    imageAlt?: string;
  }[];
  ctaFormSectionTitle?: string;
  ctaFormTitle?: string;
  ctaFormSections?: {
    title?: string;
    description?: Document;
    link?: {
      title?: string;
      url?: string;
    };
  }[];
  partnerCasesSectionTitle?: string;
  partnerCases?: {
    logo?: string;
    logoAlt: string;
    imageUrl?: string;
    imageAlt: string;
    text?: Document;
    link?: {
      title?: string;
      url?: string;
    };
  }[];
  fundArticles?: {
    title?: string;
    publishedAt: string;
    duration: number;
    url: string;
    externalLink?: string;
    image: {
      url?: string;
      alt: string;
      width: number;
      height: number;
    };
    label: string;
  }[];
  fundMetricsSectionTitle?: string;
  fundMetrics?: {
    metricValue?: string;
    metricName?: string;
    description?: string;
  }[];
  secondCampaignSectionTitle?: string;
  secondCampaignText?: Document;
  secondCampaignButtons?: HighlightButton[];
  secondCampaignImage?: {
    url: string;
    alt: string;
    credit?: string;
  };
  secondCampaignImageFit?: TypeImpactFundTemplate['fields']['secondCampaignImageFit'];
  fundSlug?: string;
  previewInitiatives: {
    id: string;
    pillarName?: string;
    pillarColor?: string;
    image?: Asset<'WITHOUT_UNRESOLVABLE_LINKS'>;
    iconUrl?: string;
    iconAlt?: string;
    name: string;
    location?: string;
    description?: string;
    slug?: string;
  }[];
};

function ImpactFundPage(props: ImpactFundTemplateProps) {
  const {
    heroCTAs,
    heroImage,
    heroParagraph,
    title,
    partnerLogosTitle,
    partnerLogos,
    pitchSectionTitle,
    pitchText,
    ourSolutionButtons,
    fundPillarsWithData,
    ctaSectionTitle,
    ctaSectionText,
    ctaSectionButtons,
    ctaSectionBackgroundImage,
    campaignSectionTitle,
    campaignText,
    campaignButtons,
    campaignImage,
    campaignImageFit,
    ourApproachSectionTitle,
    ourApproachText,
    ourApproachLogos,
    ourApproachSteps,
    fundPillarsSectionTitle,
    fundPillarsTitle,
    fundPillarsParagraph,
    faqSectionTitle,
    faqSectionReferences,
    fundManagerSectionTitle,
    fundManagerTitle,
    fundManagerParagraph,
    fundManagerHeadline,
    advisoryGroupHeadline,
    fundManagerProfiles,
    advisoryGroupProfiles,
    ctaFormSectionTitle,
    ctaFormTitle,
    ctaFormSections,
    partnerCasesSectionTitle,
    partnerCases,
    fundArticles,
    fundMetricsSectionTitle,
    fundMetrics,
    secondCampaignSectionTitle,
    secondCampaignText,
    secondCampaignButtons,
    secondCampaignImage,
    secondCampaignImageFit,
    fundSlug,
    previewInitiatives,
  } = props;

  return (
    <>
      <FundHero
        title={title}
        paragraph={heroParagraph}
        imageCredit={heroImage?.credit}
        imageUrl={heroImage?.url}
        imageAlt={heroImage?.alt}
        primaryButtonLabel={heroCTAs?.[0]?.label}
        primaryButtonHref={heroCTAs?.[0]?.href}
        secondaryButtonLabel={heroCTAs?.[1]?.label}
        secondaryButtonHref={heroCTAs?.[1]?.href}
      />
      <PartnerLogosLayout title={partnerLogosTitle} logosList={partnerLogos} />

      {/* Pitch section */}
      <HighlightLayout
        sectionTitle={pitchSectionTitle}
        text={pitchText}
        buttons={ourSolutionButtons}
        images={fundPillarsWithData.map((pillar) => ({
          imageUrl: pillar.image?.url,
          imageAlt: pillar.image?.alt ?? '',
          imageText: pillar.title,
          imageIconUrl: pillar.icon?.url,
          imageIconAlt: pillar.icon?.alt ?? '',
        }))}
        backgroundColor={colors.grey50}
      />

      {/* Campaign section 1 */}
      <HighlightLayout
        rightAlignedCopy
        sectionTitle={campaignSectionTitle}
        text={campaignText}
        buttons={campaignButtons}
        images={
          campaignImage
            ? [
                {
                  imageUrl: campaignImage.url,
                  imageAlt: campaignImage.alt,
                  imageText: campaignImage.credit,
                  objectFit: campaignImageFit,
                },
              ]
            : undefined
        }
      />

      <OurApproachLayout
        sectionTitle={ourApproachSectionTitle}
        text={ourApproachText}
        logosList={ourApproachLogos}
        steps={ourApproachSteps}
      />

      <LazyComponent loadOffset={500} height="600px">
        <MapMetricsLayout
          sectionTitle={fundMetricsSectionTitle}
          mapItems={fundPillarsWithData.map((pillar) => ({
            label: pillar.title,
            locationCodes: pillar.locationCodes,
          }))}
          metrics={fundMetrics}
        />
      </LazyComponent>

      <CTASectionLayout
        sectionTitle={ctaSectionTitle}
        title={ctaSectionText}
        buttons={ctaSectionButtons}
        backgroundImageUrl={ctaSectionBackgroundImage?.url}
        backgroundImageAlt={ctaSectionBackgroundImage?.alt ?? ''}
      />

      <FundPillars
        sectionTitle={fundPillarsSectionTitle}
        fundPillarsWithData={fundPillarsWithData}
        fundPillarsTitle={fundPillarsTitle}
        fundPillarsParagraph={fundPillarsParagraph}
        fundSlug={fundSlug}
      />

      {previewInitiatives.length > 0 && (
        <ProjectListingWithPreview
          sectionTitle="other projects within the fund"
          ctaTitle="See all other projects"
          ctaDescription="Browse all other projects within the fund"
          ctaButtonLabel="see all projects"
          fundSlug={fundSlug}
          backgroundColor={colors.grey50}
          initiatives={previewInitiatives}
        />
      )}

      <PartnerCasesLayout sectionTitle={partnerCasesSectionTitle} partnerCases={partnerCases} />

      <HighlightedProfilesLayout
        sectionTitle={fundManagerSectionTitle}
        title={fundManagerTitle}
        paragraph={fundManagerParagraph}
        primaryProfilesHeadline={fundManagerHeadline}
        secondaryProfilesHeadline={advisoryGroupHeadline}
        primaryProfiles={fundManagerProfiles}
        secondaryProfiles={advisoryGroupProfiles}
      />

      {/* Campaign section 2 */}
      <HighlightLayout
        rightAlignedCopy
        sectionTitle={secondCampaignSectionTitle}
        text={secondCampaignText}
        buttons={secondCampaignButtons}
        images={
          secondCampaignImage
            ? [
                {
                  imageUrl: secondCampaignImage.url,
                  imageAlt: secondCampaignImage.alt,
                  imageText: secondCampaignImage.credit,
                  objectFit: secondCampaignImageFit,
                },
              ]
            : undefined
        }
      />

      <QuestionsAndAnswers
        sectionTitle={faqSectionTitle}
        faqSectionReferences={faqSectionReferences}
        backgroundColor={colors.grey50}
      />

      <CarouselLayout
        sectionTitle={'Articles'}
        items={fundArticles}
        backgroundColor={colors.grey50}
      />

      <FormCTA
        ctaFormSectionTitle={ctaFormSectionTitle}
        ctaFormTitle={ctaFormTitle}
        ctaFormSections={ctaFormSections}
      />
    </>
  );
}

type ServerSideProps = {
  props: ImpactFundTemplateProps;
} & WithPageAppProps;

export async function getImpactFundPageProps({
  page,
  name,
  HOST_URL,
}: {
  page: TypeImpactFundTemplate;
  name: string;
  HOST_URL: string;
}): Promise<ServerSideProps> {
  const category = page.fields.impactFund;

  if (!category) throw new Error('No fund connected to page');
  const isCDRCategory = await isCDRCategoryId(category.sys.id);

  const [initiatives, pillars] = await Promise.all([
    isCDRCategory
      ? { result: [] }
      : getInitiatives({
          categoryId: [category.sys.id],
          status: ['ACTIVE'],
          limit: 100,
          skipContentfulData: true,
        }),
    getPillars({
      categoryId: [category.sys.id],
      skipContentfulData: true,
    }),
  ]);

  const hasPillars = pillars.result.length > 0;

  const [contentfulInitiatives, contentfulPillars, contentfulFundArticles, ...approaches] =
    await Promise.all([
      isCDRCategory
        ? { items: [] }
        : getEntries<TypeInitiativeSkeleton>({
            content_type: 'initiative',
            'sys.id[in]': initiatives.result.map((initiative) => initiative.id),
          }),
      hasPillars
        ? getEntries<TypePillarSkeleton>({
            content_type: 'pillar',
            'sys.id[in]': pillars.result.map((pillar) => pillar.id),
          })
        : { items: [] },
      getEntries<TypeBlogPostSkeleton>({
        content_type: 'blogPost',
        'fields.availableFor': 'Main web',
        links_to_entry: category.sys.id,
        limit: 9,
        order: ['-fields.publishedAt'],
      }),

      ...(hasPillars
        ? pillars.result.map((pillar) =>
            getApproaches({
              pillarId: [pillar.id],
            }),
          )
        : []),
    ]);

  const pillarsWithData = contentfulPillars.items.map((pillar) => {
    const pillarSpecificInitiativeIds = initiatives.result
      .filter((initiative) => initiative.pillarIds.includes(pillar.sys.id))
      .map(({ id }) => id);

    const randomizedInitiativeId =
      pillarSpecificInitiativeIds[
        pillarSpecificInitiativeIds.length > 0
          ? Math.floor(Math.random() * (pillarSpecificInitiativeIds.length - 1))
          : 0
      ];

    const randomizedInitiative = contentfulInitiatives.items.find(
      (item) => item.sys.id === randomizedInitiativeId,
    )?.fields;

    const pillarInitiatives = contentfulInitiatives.items
      .filter((initiative) => pillarSpecificInitiativeIds.includes(initiative.sys.id))
      .map((initiative) => {
        const locationCodes =
          initiatives.result.find((i) => i.id === initiative.sys.id)?.locationCodes ?? [];

        return {
          locationCodes,
          id: initiative.sys.id,
          pillarName: pillar.fields.title,
          image: initiative.fields.image,
          iconUrl: initiative.fields.logo?.fields.file?.url,
          iconAlt:
            initiative.fields.logo?.fields.description ??
            initiative.fields.logo?.fields.title ??
            '',
          name: initiative.fields.title ?? initiative.fields.name,
          description: initiative.fields.description,
          slug: initiative.fields.slug,
        };
      });

    const locationCodes = pillarInitiatives.flatMap((initiative) => initiative.locationCodes);

    return {
      title: pillar.fields.title,
      description: pillar.fields.description,
      selectionCriteria: pillar.fields.selectionCriteria,
      image: toImageFromContentfulMedia(pillar.fields.image),
      icon: toImageFromContentfulMedia(pillar.fields.icon),
      locationCodes,
      approaches: approaches
        .flatMap((approachResult) => approachResult.result)
        .filter((approach) =>
          approach.initiativeIds.some((initiativeId) =>
            pillarSpecificInitiativeIds.includes(initiativeId),
          ),
        )
        .map((approach) => ({
          id: approach.id,
          name: approach.name,
          icon:
            approach.icon && approach.icon.file?.url
              ? {
                  url: approach.icon.file?.url,
                  alt: approach.icon.description ?? approach.icon.title ?? '',
                }
              : undefined,
        })),
      randomizedInitiative: {
        description: randomizedInitiative?.description,
        slug: randomizedInitiative?.slug,
        title: randomizedInitiative?.title,
        logo: toImageFromContentfulMedia(randomizedInitiative?.logo),
      },
      previewInitiative: pillarInitiatives[0],
    };
  });

  const previewInitiatives = pillarsWithData
    .map((pillar) => pillar.previewInitiative)
    .filter((initiative) => initiative);
  const locations = await getLocations({
    query: { code: previewInitiatives.flatMap((initiative) => initiative.locationCodes) },
  });

  const previewInitiativesWithLocation = previewInitiatives.map(
    ({ locationCodes, ...initiative }, index) => {
      const initiativeLocations = locations.result.filter((location) =>
        locationCodes.includes(location.code),
      );
      const { truncated, countriesTruncated } = getLocationStrings(initiativeLocations);

      return {
        ...initiative,
        pillarColor: PILLAR_COLORS[index % PILLAR_COLORS.length],
        location: countriesTruncated.length ? countriesTruncated : truncated,
      };
    },
  );

  return {
    props: {
      heroCTAs: page.fields.heroCTAs?.filter(hasValue).map((cta) => ({
        label: cta.fields.identifier,
        href: getLinkHref(cta),
      })),
      heroImage: toImageFromContentfulMedia(category.fields.topMedia),
      heroParagraph: page.fields.heroParagraph,
      title: category.fields.title,
      partnerLogosTitle: page.fields.partnerLogosTitle,
      partnerLogos: page.fields.partnerLogos?.map((partnerLogo) => ({
        link: partnerLogo?.fields.link,
        image: {
          url: partnerLogo?.fields.image?.fields.file?.url ?? '',
          name: partnerLogo?.fields.image?.fields.file?.fileName,
          alt:
            partnerLogo?.fields.image?.fields.description ??
            partnerLogo?.fields.image?.fields.title ??
            '',
        },
      })),
      introHeadline: category.fields.introHeadline,
      description: category.fields.description,
      ourSolutionButtons: linksToButtons(page.fields.ourSolutionButtons),
      pitchSectionTitle: page.fields.pitchSectionTitle,
      pitchText: page.fields.pitchText,
      fundPillarsWithData: pillarsWithData,
      ctaSectionTitle: page.fields.ctaSectionTitle,
      ctaSectionText: page.fields.ctaSectionText,
      ctaSectionButtons: page.fields.ctaSectionButtons?.filter(hasValue).map((cta) => ({
        label: cta.fields.identifier,
        href: getLinkHref(cta),
      })),
      ctaSectionBackgroundImage: toImageFromContentfulMedia(page.fields.ctaSectionBackgroundImage),
      campaignSectionTitle: page.fields.campaignSectionTitle,
      campaignText: page.fields.campaignText,
      campaignButtons: linksToButtons(page.fields.campaignButtons),
      campaignImage: toImageFromContentfulMedia(page.fields.campaignImage),
      campaignImageFit: page.fields.campaignImageFit,
      ourApproachSectionTitle: page.fields.ourApproachSectionTitle,
      ourApproachText: page.fields.ourApproachText,
      ourApproachLogos: page.fields.ourApproachLogos?.map((logo) => ({
        link: logo?.fields.link,
        image: {
          url: logo?.fields.image?.fields.file?.url ?? '',
          fileName: logo?.fields.image?.fields.file?.fileName,
          alt: logo?.fields.image?.fields.description ?? logo?.fields.image?.fields.title ?? '',
        },
      })),
      ourApproachSteps: page.fields.ourApproachSteps?.map((step) => ({
        title: step?.fields.title,
        description: step?.fields.description,
      })),
      fundPillarsSectionTitle: page.fields.fundPillarsSectionTitle,
      fundPillarsTitle: page.fields.fundPillarsTitle,
      fundPillarsParagraph: page.fields.fundPillarsParagraph,
      faqSectionTitle: page.fields.faqSectionTitle,
      faqSectionReferences: page.fields.faqSectionReferences?.filter(hasValue).map((faq) => ({
        title: faq?.fields.title,
        description: faq?.fields.description,
      })),
      fundManagerSectionTitle: page.fields.fundManagersSectionTitle,
      fundManagerTitle: page.fields.fundManagersTitle,
      fundManagerParagraph: page.fields.fundManagersParagraph,
      fundManagerHeadline: page.fields.fundManagersHeadline,
      advisoryGroupHeadline: page.fields.advisoryGroupHeadline,
      fundManagerProfiles: category.fields.fundManagerProfiles?.map((profile) => ({
        name: profile?.fields.name,
        title: profile?.fields.title,
        description: profile?.fields.profileDescription,
        imageUrl:
          profile?.fields.cutoutImage?.fields.file?.url || profile?.fields.image?.fields.file?.url,
        imageAlt:
          profile?.fields.cutoutImage?.fields.description ??
          profile?.fields.cutoutImage?.fields.title ??
          profile?.fields.image?.fields.description ??
          profile?.fields.image?.fields.title ??
          '',
      })),
      advisoryGroupProfiles: category.fields.advisoryGroupProfiles?.map((profile) => ({
        name: profile?.fields.name,
        title: profile?.fields.title,
        imageUrl:
          profile?.fields.cutoutImage?.fields.file?.url || profile?.fields.image?.fields.file?.url,
        imageAlt:
          profile?.fields.cutoutImage?.fields.description ??
          profile?.fields.cutoutImage?.fields.title ??
          profile?.fields.image?.fields.description ??
          profile?.fields.image?.fields.title ??
          '',
      })),
      ctaFormSectionTitle: page.fields.contactUsSectionTitle,
      ctaFormTitle: page.fields.contactUsTitle,
      ctaFormSections: toFormCTASection(page.fields.contactUsSections),
      partnerCasesSectionTitle: page.fields.partnerCasesSectionTitle,
      partnerCases: page.fields.partnerCases?.map((partnerCase) => ({
        logo: partnerCase?.fields.mediaLogo?.fields.imageLandscape?.fields.file?.url,
        logoAlt:
          partnerCase?.fields.mediaLogo?.fields.imageLandscape?.fields.description ??
          partnerCase?.fields.mediaLogo?.fields.imageLandscape?.fields.title ??
          '',
        imageUrl:
          partnerCase?.fields.mediaBackground?.fields.orientedMedia?.fields.imageLandscape?.fields
            .file?.url,
        imageAlt:
          partnerCase?.fields.mediaBackground?.fields.orientedMedia?.fields.imageLandscape?.fields
            .description ??
          partnerCase?.fields.mediaBackground?.fields.orientedMedia?.fields.imageLandscape?.fields
            .title ??
          '',
        text: partnerCase?.fields.body,
        link: {
          title: partnerCase?.fields.link?.fields.identifier,
          url: getLinkHref(partnerCase?.fields.link),
        },
      })),
      fundArticles: contentfulFundArticles.items.map((article) => ({
        title: article.fields.title,
        publishedAt: article.fields.publishedAt,
        duration: getReadTime({
          body: article.fields.body?.content,
          title: article.fields.title,
          preamble: article.fields.preamble,
        }),
        url: `/articles/${article.fields.slug}`,
        externalLink: article.fields.externalLink,
        image: {
          url: article.fields.image?.fields.file?.url,
          alt: article.fields.image?.fields.description ?? '',
          height: article.fields.image?.fields.file?.details?.image?.height ?? 0,
          width: article.fields.image?.fields.file?.details?.image?.width ?? 0,
        },
        label: 'Read more',
      })),
      fundMetricsSectionTitle: page.fields.fundMetricsSectionTitle,
      fundMetrics: page.fields.fundMetrics?.map((metric) => ({
        metricValue: metric?.fields.metricValue,
        metricName: metric?.fields.title,
        description: metric?.fields.description,
      })),
      secondCampaignSectionTitle: page.fields.secondCampaignSectionTitle,
      secondCampaignText: page.fields.secondCampaignText,
      secondCampaignButtons: linksToButtons(page.fields.secondCampaignButtons),
      secondCampaignImage: toImageFromContentfulMedia(page.fields.secondCampaignImage),
      secondCampaignImageFit: page.fields.secondCampaignImageFit,
      fundSlug: category.fields.slug,
      previewInitiatives: previewInitiativesWithLocation,
    },
    pageAppProps: {
      metaHeader: transformMetaHeaderType({
        metaHeader: page.fields.metaHeader,
        overrides: { canonicalLink: `${HOST_URL}/impact-funds/${name}` },
        fallbacks: {
          seoTitle: category.fields.title,
          seoDescription: page.fields.heroParagraph,
          socialDescription: page.fields.heroParagraph,
          socialImageUrl:
            category.fields.topMedia?.fields.imageLandscape?.fields.file?.url ||
            category.fields.topMedia?.fields.imagePortrait?.fields.file?.url,
          socialTitle: category.fields.title,
        },
      }),
    },
  };
}

export const getServerSideProps = withAppProps(async (ctx, config) => {
  const { name } = ctx.query;

  if (!name || Array.isArray(name)) throw new Error('No name provided');

  const { items: impactFundPages } = await getEntries<TypeImpactFundTemplateSkeleton>({
    content_type: 'impactFundTemplate',
    'fields.slug': `${name}`,
    include: 10,
  });
  const [page] = impactFundPages;

  if (!page) {
    throw new UrlError(`No page found for slug ${name}`, { status: 404 });
  }

  return getImpactFundPageProps({ page, name, HOST_URL: config.HOST_URL });
});

export default ImpactFundPage;
